import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';

interface StripeProviderProps {
  children: React.ReactNode;
}

// Lazy Stripe provider: only loads the Stripe SDK when the checkout/payment page is visited
export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Detect when Stripe is actually needed (checkout routes)
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('checkout') || path.includes('payment')) {
      setShouldLoad(true);
    }

    // Listen for route changes
    const observer = new MutationObserver(() => {
      const currentPath = window.location.pathname;
      if (currentPath.includes('checkout') || currentPath.includes('payment')) {
        setShouldLoad(true);
      }
    });
    observer.observe(document.querySelector('title') || document.head, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    import('@/lib/stripe').then(mod => {
      mod.getStripe().then(setStripe);
    });
  }, [shouldLoad]);

  if (!shouldLoad || !stripe) {
    // Render children without Stripe wrapper when not needed
    return <>{children}</>;
  }

  return (
    <Elements stripe={stripe}>
      {children}
    </Elements>
  );
};
